"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";

interface HeroVideoProps {
  /** Optional MP4 source. When omitted, the poster is shown with a decorative play affordance. */
  src?: string;
  poster: string;
  title: string;
}

/**
 * Presentation video with a poster overlay and centred play button.
 * Mirrors the native-video pattern used on the formation detail page, but
 * self-contained and lazy (the video only loads once the user hits play).
 */
const HeroVideo: React.FC<HeroVideoProps> = ({ src, poster, title }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => {
    if (!src) return;
    videoRef.current?.play();
    setIsPlaying(true);
  };

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-3xl border border-[#EFEFEF] bg-[#1B365E] shadow-xl shadow-[#1B365E]/15">
      {src && (
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          preload="none"
          poster={poster}
          controls={isPlaying}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        >
          <source src={src} type="video/mp4" />
          Votre navigateur ne prend pas en charge la lecture vidéo.
        </video>
      )}

      {!isPlaying && (
        <button
          type="button"
          onClick={handlePlay}
          aria-label={src ? `Lire la vidéo : ${title}` : title}
          aria-disabled={!src}
          className="group absolute inset-0 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#40BFDE]/60"
        >
          <Image src={poster} alt={title} fill className="object-cover" priority />
          <span className="absolute inset-0 bg-linear-to-t from-[#1B365E]/60 via-transparent to-transparent" />
          <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform duration-300 group-hover:scale-110">
            <svg width="22" height="24" viewBox="0 0 22 24" fill="none" aria-hidden="true" className="ml-1">
              <path d="M20.5 10.27a2 2 0 0 1 0 3.46L3.5 23.5A2 2 0 0 1 .5 21.77V2.23A2 2 0 0 1 3.5.5l17 9.77Z" fill="#1B365E" />
            </svg>
          </span>
        </button>
      )}
    </div>
  );
};

export default HeroVideo;
